// Video QC for one campaign: its brief and checklist, and every campaign
// video (plus drafts the team uploads) with live QC status and verdicts.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { FEATURE_VIDEO_QC_SAMPLE } from 'config';
import mixpanel from 'utils/mixpanel';
import AddDraftModal from './AddDraftModal';
import ChecklistPanel from './ChecklistPanel';
import QcResultModal from './QcResultModal';
import QcVideoTable from './QcVideoTable';
import usePolling from './usePolling';
import { buildRows, isRunning, summarize } from './qcFormat';
import { errorText, loadQc } from './qcClient';
import { AiMark, HowItWorks, Skeleton } from './aiUi';
import { Button, C, CardHeader, Empty, SURFACE } from './ui';

const POLL_MS = { live: 4000, sample: 1000 };
const REFRESH_TOAST = 'video-qc-refresh';

function SampleBanner() {
    return (
        <div role="status" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: C.goldSoft, border: '1px solid #fde68a', color: '#92400e', borderRadius: 12, padding: '10px 14px', fontSize: 13 }}>
            <ScienceOutlinedIcon aria-hidden="true" style={{ fontSize: 18, marginTop: 1 }} />
            <span>
                <b>Sample data.</b> The Video QC backend isn't connected yet, so results here are made up for demo and design review.
                Runs, edits and decisions work but are not saved.
            </span>
        </div>
    );
}

// Placeholder while the campaign's QC loads: the shape of the two cards.
export function QcSkeleton({ label }) {
    return (
        <div role="status" style={{ ...SURFACE, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <AiMark size={36} active />
                <span style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{label}</span>
            </div>
            {[0, 1, 2].map((i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 160px 90px', gap: 16, alignItems: 'center' }}>
                    <Skeleton width={40} height={40} radius={10} />
                    <div><Skeleton width="62%" height={11} /><Skeleton width="38%" height={9} style={{ marginTop: 7 }} /></div>
                    <Skeleton height={20} radius={8} />
                    <Skeleton height={20} radius={8} />
                </div>
            ))}
        </div>
    );
}

// What happens to a video here, for a campaign with nothing to check yet.
const NoVideos = () => (
    <Empty icon={<AiMark size={48} />} title="No videos yet"
        extra={<HowItWorks style={{ justifyContent: 'center', marginTop: 16 }} />}>
        Link videos to this campaign, or check a draft before it goes live. The AI reviewer checks each one against the checklist above.
    </Empty>
);

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function videosSubtitle(rows) {
    const checked = rows.filter((r) => r.qc?.latest_check).length;
    const running = rows.filter((r) => r.qc && isRunning(r.qc.status)).length;
    return [plural(rows.length, 'video'), `${checked} with an AI result`, running ? `${running} checking now` : null].filter(Boolean).join(' · ');
}

const EMPTY = { status: 'loading', error: '', client: null, brief: '', checklist: [], qcVideos: [] };

export default function CampaignQc({ campaignId, videos = [], campaignBrief = '', allowSample = FEATURE_VIDEO_QC_SAMPLE, onStats = () => {} }) {
    const [state, setState] = useState(EMPTY);
    const [attempt, setAttempt] = useState(0);
    const [openRow, setOpenRow] = useState(null);
    const [addingDraft, setAddingDraft] = useState(false);
    const [startingKey, setStartingKey] = useState(null);
    const mounted = useRef(true);
    const refreshSeq = useRef(0);

    useEffect(() => () => { mounted.current = false; }, []);

    useEffect(() => {
        let active = true;
        setState(EMPTY);
        loadQc(campaignId, videos, { allowSample })
            .then((loaded) => {
                if (!active) return;
                if (loaded.unavailable) { setState({ ...EMPTY, status: 'unavailable' }); return; }
                const { client, overview } = loaded;
                setState({ status: 'ready', error: '', client, brief: overview.brief || '', checklist: overview.checklist || [], qcVideos: overview.videos || [] });
            })
            .catch((err) => active && setState({ ...EMPTY, status: 'error', error: errorText(err) }));
        return () => { active = false; };
        // Load once per campaign (and on Retry); the campaign's video list only seeds sample data.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, attempt, allowSample]);

    const { client } = state;
    const sample = client?.mode === 'sample';
    const track = useCallback((event, props) => {
        if (!sample) mixpanel.track(event, { campaign_id: campaignId, ...props });
    }, [sample, campaignId]);

    // Only the newest refresh may write: a slow older response must not
    // overwrite a newer one (and, e.g., stop polling while a video is queued).
    const refresh = useCallback(async () => {
        if (!client) return;
        refreshSeq.current += 1;
        const seq = refreshSeq.current;
        try {
            const overview = await client.overview();
            if (!mounted.current || seq !== refreshSeq.current) return;
            setState((prev) => ({ ...prev, brief: overview.brief || '', checklist: overview.checklist || [], qcVideos: overview.videos || [] }));
        } catch (err) {
            if (mounted.current) toast.error(`Couldn't refresh Video QC: ${errorText(err)}`, { id: REFRESH_TOAST });
        }
    }, [client]);

    const anyRunning = state.qcVideos.some((v) => isRunning(v.status));
    usePolling(refresh, Boolean(client) && anyRunning, POLL_MS[client?.mode] || POLL_MS.live);

    const rows = useMemo(() => buildRows(videos, state.qcVideos), [videos, state.qcVideos]);
    const stats = useMemo(() => (state.status === 'ready'
        ? { ...summarize(rows), running: rows.filter((r) => r.qc && isRunning(r.qc.status)).length }
        : null), [rows, state.status]);
    useEffect(() => { onStats(stats); }, [stats, onStats]);

    const runRow = async (row) => {
        setStartingKey(row.key);
        try {
            if (row.qc) await client.runQc(row.qc.id);
            else await client.createVideo({ source: row.source, title: row.title, external_ref: row.campaignVideoId });
            track('video_qc_run', { rerun: Boolean(row.qc), draft: row.isDraft });
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            if (mounted.current) setStartingKey(null);
            await refresh();
        }
    };

    const onDraftAdded = async (video, how) => {
        setAddingDraft(false);
        toast.success(sample ? 'Sample draft added. QC is running.' : 'Draft added. QC is running.');
        track('video_qc_draft_added', { via: how });
        await refresh();
    };

    if (state.status === 'loading') return <QcSkeleton label="Loading Video QC…" />;
    if (state.status === 'unavailable') {
        return <div style={{ ...SURFACE, padding: 20 }}><Empty icon={<AiMark size={48} />} title="Video QC isn't connected yet">The backend for this page is still being set up. Check back soon.</Empty></div>;
    }
    if (state.status === 'error') {
        return (
            <div role="alert" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, padding: 20, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 16, fontSize: 13 }}>
                <span>Couldn't load Video QC: {state.error}</span>
                <Button onClick={() => setAttempt((n) => n + 1)}>Retry</Button>
            </div>
        );
    }

    const canRun = state.checklist.length > 0;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'left' }}>
            {sample && <SampleBanner />}
            <ChecklistPanel brief={state.brief} checklist={state.checklist} client={client} campaignBrief={campaignBrief}
                sample={sample} track={track}
                onChange={(next) => setState((prev) => ({ ...prev, brief: next.brief ?? prev.brief, checklist: next.checklist }))} />
            <div style={{ ...SURFACE, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <CardHeader icon={<OndemandVideoOutlinedIcon style={{ fontSize: 21 }} />} title="Videos"
                    subtitle={videosSubtitle(rows)}
                    actions={(
                        <Button variant="primary" disabled={!canRun} title={canRun ? undefined : 'Add a checklist first'} onClick={() => setAddingDraft(true)}>
                            + Check a draft
                        </Button>
                    )} />
                {rows.length === 0 ? (
                    <NoVideos />
                ) : (
                    <QcVideoTable rows={rows} canRun={canRun} startingKey={startingKey} onRun={runRow} onOpen={setOpenRow} />
                )}
            </div>
            {addingDraft && <AddDraftModal client={client} onClose={() => setAddingDraft(false)} onAdded={onDraftAdded} />}
            {openRow?.qc && (
                <QcResultModal client={client} qcVideoId={openRow.qc.id} title={openRow.title}
                    sample={sample} track={track} onClose={() => setOpenRow(null)} onChanged={refresh} />
            )}
        </div>
    );
}
